import {useEffect, useState} from 'react';

import {delay, cancellablePromise} from '../../../lib/promiseHelper';
import {useCancellablePromises, useClickOutside} from '../../../lib/customHooks';

const DELAY_BEFORE_ZOOM_IN = 0;

export default function useDeckZoom(refContainer, itemID, allowZoomIn, allowZoomOut, onZoomIn, onZoomOut) {
  const [zoom, setZoom] = useState(false);
  const apiCancellablePromise = useCancellablePromises();

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [zoom]);

  useEffect(() => {
    if (zoom && allowZoomIn) {
      const zoomEvent = new CustomEvent('zoom', {
        detail: {
          name: 'DeckLabware',
          number: itemID,
        },
      });
      window.dispatchEvent(zoomEvent);
    }
  }, [zoom, allowZoomIn, itemID]);

  useEffect(() => {
    function handleZoom(e) {
      if (e.detail.name === 'DeckLabware' && e.detail.number !== itemID) {
        setZoom(false);
        onZoomOut(itemID);
      }
    }

    window.addEventListener('zoom', handleZoom);
    return () => window.removeEventListener('zoom', handleZoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemID]);

  useClickOutside(refContainer, () => {
    if (zoom && allowZoomOut) {
      apiCancellablePromise.clearPendingPromises();
      setZoom(false);
      if (onZoomOut) {
        onZoomOut(itemID);
      }
    }
  });

  function handleMouseEnter() {
    if (allowZoomIn) {
      apiCancellablePromise.clearPendingPromises();
      const wait = cancellablePromise(delay(DELAY_BEFORE_ZOOM_IN));
      apiCancellablePromise.appendPendingPromise(wait);

      return wait.promise
        .then(() => {
          apiCancellablePromise.removePendingPromise(wait);
          setZoom(true);
          if (onZoomIn) {
            onZoomIn(itemID);
          }
        })
        .catch((errorInfo) => {
          apiCancellablePromise.removePendingPromise(wait);
          if (!errorInfo.isCanceled) {
            throw errorInfo.error;
          }
        });
    }
    return null;
  }

  function handleClick() {
    apiCancellablePromise.clearPendingPromises();
    // setTimeOut to handle clear incoming mouseEnter triggered by name input change (from non-edit to edit view)
    setTimeout(() => {
      apiCancellablePromise.clearPendingPromises();
    }, DELAY_BEFORE_ZOOM_IN / 2);
  }

  function handleMouseLeave() {
    apiCancellablePromise.clearPendingPromises();
    if (allowZoomOut) {
      setZoom(false);
      if (onZoomOut) {
        onZoomOut(itemID);
      }
    }
  }

  function handleDragStart() {
    apiCancellablePromise.clearPendingPromises();
  }

  useEffect(() => {
    const deck = refContainer.current;
    if (!deck) {
      return undefined;
    }

    deck.addEventListener('mouseenter', handleMouseEnter);
    deck.addEventListener('click', handleClick);
    deck.addEventListener('mouseleave', handleMouseLeave);
    deck.addEventListener('dragstart', handleDragStart);
    return () => {
      deck.removeEventListener('mouseenter', handleMouseEnter);
      deck.removeEventListener('click', handleClick);
      deck.removeEventListener('mouseleave', handleMouseLeave);
      deck.removeEventListener('dragstart', handleDragStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refContainer, zoom, allowZoomIn, allowZoomOut]);

  return zoom;
}
